import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["dialog", "backdrop", "closeBtn"];

  connect() {
    this.boundKeydown = this.keydown.bind(this);
    this.boundClose = () => this.close();

    // Cache refs before moving — .top-nav is position:sticky + z-index,
    // which creates a stacking context that traps any fixed-position
    // descendant no matter how high its own z-index is (it loses to
    // siblings outside .top-nav, like .bottom-nav, on DOM order). Moving
    // the dialog to <body> fixes stacking, but also moves it out of this
    // controller's own element — so its data-action bindings (which
    // resolve by walking up to the nearest matching data-controller
    // ancestor at click time) stop finding this controller. Bind those
    // listeners directly instead of relying on data-action for anything
    // inside the dialog.
    this.dialog = this.dialogTarget;
    this.backdrop = this.backdropTarget;
    this.closeBtn = this.closeBtnTarget;

    this.backdrop.addEventListener("click", this.boundClose);
    this.closeBtn.addEventListener("click", this.boundClose);
    // Cross-boundary close request — e.g. locale-switcher waits for this
    // popover to finish its close transition before navigating away.
    window.addEventListener("settings-popover:close", this.boundClose);

    document.body.appendChild(this.dialog);
  }

  disconnect() {
    document.removeEventListener("keydown", this.boundKeydown);
    window.removeEventListener("settings-popover:close", this.boundClose);
    this.backdrop.removeEventListener("click", this.boundClose);
    this.closeBtn.removeEventListener("click", this.boundClose);
    this.dialog.remove();
  }

  open() {
    this.dialog.hidden = false;
    requestAnimationFrame(() => this.dialog.classList.add("settings-popover__dialog--open"));
    document.addEventListener("keydown", this.boundKeydown);
  }

  close() {
    this.dialog.classList.remove("settings-popover__dialog--open");
    document.removeEventListener("keydown", this.boundKeydown);

    // The transition lives on __panel/__backdrop, not this outer __dialog
    // wrapper — checking the wrapper's own transitionDuration always reads
    // 0s, which skipped the wait entirely.
    const panel = this.dialog.querySelector(".settings-popover__panel");
    const duration = parseFloat(getComputedStyle(panel).transitionDuration) * 1000;
    if (duration > 0) {
      panel.addEventListener("transitionend", () => { this.dialog.hidden = true; }, { once: true });
    } else {
      this.dialog.hidden = true;
    }
  }

  keydown(e) {
    if (e.key === "Escape") this.close();
  }
}
